import { GeoJSONSource, type Map as MlMap } from 'maplibre-gl'
import type { VehicleTrack } from '../../lib/types'

/**
 * One vehicle's actual driven track, drawn over the corridor.
 *
 * "Show me the route" from a supervisor means the road this bus really took, not the plan
 * it was given -- the whole reason to look is that the two might differ. It is drawn in
 * the brand teal so it separates cleanly from the planned corridor's blue, with a halo so
 * it stays readable over the basemap, and direction arrows so a stationary screenshot
 * still says which way the bus was going.
 */

export const SRC_TRACK = 'veh-track'
export const LYR_TRACK_HALO = 'veh-track-halo'
export const LYR_TRACK = 'veh-track-line'
export const LYR_TRACK_DIR = 'veh-track-dir'
export const LYR_TRACK_ENDS = 'veh-track-ends'

const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

export function trackFeatures(t: VehicleTrack | null): GeoJSON.FeatureCollection {
  if (!t || t.track.length < 2) return empty
  const coords = t.track.map((p) => [p.lon, p.lat])
  const features: GeoJSON.Feature[] = [
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords },
      properties: { vehicle_id: t.vehicle_id, kind: 'line' },
    },
  ]
  if (t.show_ends !== false) {
    const first = t.track[0]!
    const last = t.track[t.track.length - 1]!
    features.push(
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [first.lon, first.lat] },
        properties: { kind: 'start', label: 'start' },
      },
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [last.lon, last.lat] },
        properties: { kind: 'end', label: 'now' },
      },
    )
  }
  return { type: 'FeatureCollection', features }
}

/** `before`: same optional anchor `addHeatLayers`/`addReplayAlertLayers` take -- added
 *  (round-3 adversarial review, LOW) so MapCanvas.tsx can insert this BELOW the replay
 *  alert-dot layer instead of always at the absolute top of the style, where it painted
 *  over the dots (and over the vehicle icons) regardless of call order. */
export function addTrackLayers(map: MlMap, before?: string): void {
  const anchor = before && map.getLayer(before) ? before : undefined
  if (!map.getSource(SRC_TRACK)) map.addSource(SRC_TRACK, { type: 'geojson', data: empty })

  if (!map.getLayer(LYR_TRACK_HALO)) {
    map.addLayer({
      id: LYR_TRACK_HALO,
      type: 'line',
      source: SRC_TRACK,
      filter: ['==', ['get', 'kind'], 'line'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#07080F',
        'line-opacity': 0.85,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 5, 14, 11],
      },
    }, anchor)
  }
  if (!map.getLayer(LYR_TRACK)) {
    map.addLayer({
      id: LYR_TRACK,
      type: 'line',
      source: SRC_TRACK,
      filter: ['==', ['get', 'kind'], 'line'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#00C896',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 2, 14, 5],
      },
    }, anchor)
  }
  if (!map.getLayer(LYR_TRACK_ENDS)) {
    map.addLayer({
      id: LYR_TRACK_ENDS,
      type: 'circle',
      source: SRC_TRACK,
      filter: ['!=', ['get', 'kind'], 'line'],
      paint: {
        'circle-radius': 5,
        'circle-color': ['case', ['==', ['get', 'kind'], 'end'], '#00C896', '#07080F'],
        'circle-stroke-color': '#00C896',
        'circle-stroke-width': 2,
      },
    }, anchor)
  }
}

export function setTrackData(map: MlMap, t: VehicleTrack | null): void {
  const src = map.getSource(SRC_TRACK)
  if (src instanceof GeoJSONSource) src.setData(trackFeatures(t))
}
